/* eslint complexity: warn */
import type { DataTransferObject } from '../../../@types/DataTransferObject.js';
import { type PaymentInfoType } from '../../../payment/createPaymentInfo.js';
import { logger } from '../../../utils/index.js';
import type { Wager } from '../../../wager/Wager.js';
import { getProductsString, numerous } from '../utils/utils.js';

const getFormattedAmount = (
  currencyFormatter:
    | ((amount: number | string, options?: { shouldUseDecimals?: boolean; unit?: string }) => string)
    | undefined,
  amount: number | string
): string => {
  return currencyFormatter ? currencyFormatter(amount) : `${amount} kr`;
};

type GetPaymentMessageArg = {
  paidItems: readonly Wager[];
  unpaidItems: readonly Wager[];
  paymentDTO: DataTransferObject[];
  paymentInfo: PaymentInfoType;
  currencyFormatter?: (amount: number | string, options?: { shouldUseDecimals?: boolean; unit?: string }) => string;
};

/**
 * Function to get payment message for customer.
 *
 * @param root0 params
 * @param root0.paidItems The paid items in transaction.
 * @param root0.unpaidItems The unpaid items in transaction.
 * @param root0.paymentDTO The payment data transfer object.
 * @param root0.paymentInfo The payment info for transaction.
 * @param root0.currencyFormatter Optional currency formatter function to format prices in the dialog messages
 * @returns The payment message for customer.
 */
export function getPaymentMessage({
  paidItems,
  unpaidItems,
  paymentDTO,
  paymentInfo,
  currencyFormatter,
}: GetPaymentMessageArg): [string | undefined, string | undefined] {
  const headline = [];
  const footnote: string[] = [];
  const totalPaidAmount = paymentDTO.reduce((sum: number, item: DataTransferObject) => sum + item.cost, 0);
  const totalPayRequestAmount = paidItems.reduce((sum, item) => sum + item.price, 0);
  const marketplaceWagers = paidItems.filter((wager) => wager.activatedDrawId);

  const isRetailer = marketplaceWagers.some((wager) => wager.customer === 'Retailer');

  const isOwnRows = [...paidItems, ...unpaidItems].every((wager) => wager.hasOwnRows);
  const totalPaid = paidItems.length;
  const totalUnpaid = unpaidItems.length;
  let numberOfPaidProducts = totalPaid;
  if (isOwnRows && totalPaid) {
    // When it is own rows we want to present to customer as one bet
    numberOfPaidProducts = 1;
  }

  // const competitionWager = paidWagers.find((wager) => wager.isCompetition());
  // let compDesc;
  // if (competitionWager) {
  //   const paymentInfoData = paymentInfo.get();
  //   const postPaymentInfo = paymentInfoData.filter((info) => (info.type === paymentInfoType.POST && info.localId === competitionWager.localId))?.pop();
  //   compDesc = postPaymentInfo.extraData.compName;
  //   const isCompetitionSignUp = postPaymentInfo.extraData.isCompetitionSignUp;
  //   isCompetitionSignUp && footnote.unshift(`Du är anmäld till ${compDesc}. <br /> <br />`);
  // }

  // Used only for challenge where impossible rows are calculated in ITS.
  // Calculate number of impossible rows and present to customer if any
  const diffAmount = totalPayRequestAmount - totalPaidAmount;
  const challengeWager = paidItems.find((wager) => wager.productBet?.product === 'Challenge');
  let impossibleRows;
  const challengeProductBet = challengeWager?.productBet;

  if (challengeProductBet) {
    impossibleRows = diffAmount / challengeProductBet.rowPriceMulti / challengeProductBet.rowPrice;
  }

  if (impossibleRows) {
    logger.info('Impossible rows found, showing customer dialog', {
      impossibleRows,
      paidAmount: totalPaidAmount,
      requestedAmount: totalPayRequestAmount,
    });
    headline.push(
      'Dina kombinationer går inte att vinna på',
      `Ditt system innehöll ${impossibleRows} rad${impossibleRows > 1 ? 'er' : ''}
      med kombinationer som inte är möjliga att vinna på. Den nya systemkostnaden blev
      ${getFormattedAmount(currencyFormatter, totalPaidAmount)}.<br />`
    );
    if (isRetailer) {
      footnote.push(`<br /> Du kan nu stänga fliken.`);
    } else {
      footnote.push(
        numerous(numberOfPaidProducts)(['det', 'dem'])(
          `Du hittar {#0} under Mina spel${marketplaceWagers.length > 0 ? ` och på lagsidan` : '.'}`
        )
      );
    }
    return [headline.join(' '), footnote.join(' ')];
  }

  if (totalPaid) {
    const paidProducts = getProductsString(paymentInfo, paidItems);
    headline.push(
      numerous(numberOfPaidProducts)(['Ditt', 'Dina'], ['betalat', 'betalade'])(`{#0} spel på ${paidProducts} är {#1}`)
    );
    if (isRetailer) {
      footnote.push(
        numerous(numberOfPaidProducts)(['Spelet', 'Spelen'], ['det', 'dem'])(
          `{#0} kostade ${getFormattedAmount(currencyFormatter, totalPaidAmount)}. Du kan nu stänga fliken.`
        )
      );
    } else {
      footnote.push(
        numerous(numberOfPaidProducts)(['Spelet', 'Spelen'], ['det', 'dem'])(
          `{#0} kostade ${getFormattedAmount(currencyFormatter, totalPaidAmount)}. Du hittar${/* compDesc ? ` det i ${compDesc}-fliken och` : */ ` {#1}`} under Mina spel${marketplaceWagers.length > 0 ? ` och på lagsidan` : '.'}`
        )
      );
    }
  }

  if (totalUnpaid) {
    if (isOwnRows && unpaidItems.length > 0 && paidItems.length > 0) {
      const totalUnpaidAmount = unpaidItems.reduce((sum, wager) => sum + wager.price, 0);
      const totalPrice = totalPaidAmount + totalUnpaidAmount;
      const message = [
        `Ett fel uppstod, endast ${getFormattedAmount(currencyFormatter, totalPaidAmount)} av ${getFormattedAmount(
          currencyFormatter,
          totalPrice
        )} gick att betala.`,
        'Du kan annullera betalade delar av spelet under Mina spel.',
      ];
      footnote.push(`<br />${message.join('<br />')}`);
    } else {
      const errorMessage = numerous(totalUnpaid)(['något', 'några'], ['din kupong', 'dina kuponger'])(
        `{#0} av dina spel gick inte igenom, vänligen kontrollera {#1}`
      );
      if (totalPaid) {
        headline.push(`men ${errorMessage}`);
      } else if (errorMessage) {
        headline.push(`${errorMessage.charAt(0).toUpperCase()}${errorMessage.slice(1)}`);
      }
    }
  }

  // const grasroten = await getGrasrotenMessageForDialogOrToaster();
  // if (grasroten.hasSponsorship) {
  //   footnote.push(`<br /> ${grasroten.thankYouMessage}`);
  // }

  const finalHeadline = headline.join(' ');
  const finalFootnote = footnote.join(' ');

  return [finalHeadline || undefined, finalFootnote || undefined] as const;
}
